import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { RotateCcw, Bot, User, Hand } from "lucide-react";
import axios from "axios";
import { useLang, STR } from "@/i18n";
import { sfx } from "@/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rnd = () => Math.floor(Math.random() * 6);

export default function ParOuImpar() {
  const { lang } = useLang();
  const t = STR[lang];
  const [phase, setPhase] = useState("side");
  const [side, setSide] = useState(null);
  const [myNum, setMyNum] = useState(null);
  const [aiNum, setAiNum] = useState(null);
  const [score, setScore] = useState({ you: 0, ai: 0 });
  const timer = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 16 });

  useEffect(() => () => clearTimeout(timer.current), []);

  const sum = myNum !== null && aiNum !== null ? myNum + aiNum : null;
  const isEven = sum !== null && sum % 2 === 0;
  const youWin = sum !== null && ((isEven && side === "par") || (!isEven && side === "impar"));

  const pickSide = (s) => {
    setSide(s);
    setPhase("fingers");
  };

  const pickNumber = (n) => {
    if (phase !== "fingers") return;
    const ai = rnd();
    setMyNum(n);
    setAiNum(ai);
    setPhase("reveal");
    sfx.move();
    const total = n + ai;
    const even = total % 2 === 0;
    const won = (even && side === "par") || (!even && side === "impar");
    if (won) sfx.win();
    else sfx.lose();
    axios.post(`${API}/score`, { result: won ? "you" : "ai" }).catch(() => {});
    setScore((sc) => (won ? { ...sc, you: sc.you + 1 } : { ...sc, ai: sc.ai + 1 }));
    timer.current = setTimeout(() => {
      setMyNum(null);
      setAiNum(null);
      setPhase("fingers");
    }, 2800);
  };

  const reset = () => {
    clearTimeout(timer.current);
    setPhase("side");
    setSide(null);
    setMyNum(null);
    setAiNum(null);
    setScore({ you: 0, ai: 0 });
  };

  const status =
    phase === "side"
      ? { text: t.pi_choose, cls: "text-slate-300" }
      : phase === "fingers"
        ? { text: side === "par" ? t.pi_you_par : t.pi_you_impar, cls: "text-sky-300" }
        : youWin
          ? { text: t.st_win, cls: "text-sky-300" }
          : { text: t.st_lose, cls: "text-amber-300" };

  return (
    <div
      data-testid="parimpar-game"
      className="relative"
      style={{ perspective: 1200 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_40%,rgba(46,107,255,0.18),rgba(251,191,36,0.1)_45%,transparent_70%)] blur-2xl pointer-events-none" />

      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative"
      >
        <div className="rounded-[2rem] border border-white/10 bg-[#0c1122]/90 backdrop-blur-xl p-5 sm:p-7 shadow-[0_40px_120px_-20px_rgba(46,107,255,0.35)]">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30">
                <User className="w-4 h-4 text-sky-300" />
              </span>
              <div>
                <p className="text-xs font-bold text-white leading-none">{t.you}</p>
                <p className="mono text-[10px] text-sky-300 mt-1">{score.you} {t.wins}</p>
              </div>
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-slate-500">vs</span>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-xs font-bold text-white leading-none">DUELO IA</p>
                <p className="mono text-[10px] text-amber-300 mt-1">{score.ai} {t.wins}</p>
              </div>
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30">
                <Bot className="w-4 h-4 text-amber-300" />
              </span>
            </div>
          </div>

          <div className="relative h-[290px] sm:h-[320px]">
            <AnimatePresence mode="wait">
              {phase === "side" && (
                <motion.div
                  key="side"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-6"
                >
                  <p className="mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
                    {t.pi_side}
                  </p>
                  <div className="grid grid-cols-2 gap-4 w-full max-w-[320px]">
                    <motion.button
                      onClick={() => pickSide("par")}
                      data-testid="parimpar-side-par"
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex flex-col items-center gap-2 rounded-3xl border border-sky-400/30 bg-sky-400/10 py-7 font-display font-black text-xl text-sky-300 transition-colors hover:bg-sky-400/20"
                    >
                      <Hand className="w-6 h-6" />
                      {t.pi_par}
                    </motion.button>
                    <motion.button
                      onClick={() => pickSide("impar")}
                      data-testid="parimpar-side-impar"
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex flex-col items-center gap-2 rounded-3xl border border-amber-400/30 bg-amber-400/10 py-7 font-display font-black text-xl text-amber-300 transition-colors hover:bg-amber-400/20"
                    >
                      <Hand className="w-6 h-6" />
                      {t.pi_impar}
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {phase === "fingers" && (
                <motion.div
                  key="fingers"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-5"
                >
                  <span
                    className={`mono text-[10px] uppercase tracking-[0.22em] border rounded-full px-3 py-1.5 ${
                      side === "par"
                        ? "border-sky-400/30 bg-sky-400/10 text-sky-300"
                        : "border-amber-400/30 bg-amber-400/10 text-amber-300"
                    }`}
                  >
                    {t.you} · {side === "par" ? t.pi_par : t.pi_impar}
                  </span>
                  <p className="text-sm text-slate-400">{t.pi_fingers}</p>
                  <div className="grid grid-cols-3 gap-3 w-full max-w-[250px]">
                    {[0, 1, 2, 3, 4, 5].map((n) => (
                      <motion.button
                        key={n}
                        data-testid={`parimpar-num-${n}`}
                        onClick={() => pickNumber(n)}
                        whileHover={{ scale: 1.1, borderColor: "rgba(56,189,248,0.5)" }}
                        whileTap={{ scale: 0.9 }}
                        className="aspect-square rounded-2xl border border-white/10 bg-white/[0.04] font-display font-black text-2xl text-white hover:text-sky-300 transition-colors"
                      >
                        {n}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {phase === "reveal" && sum !== null && (
                <motion.div
                  key="reveal"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                >
                  <div className="flex items-center gap-4 sm:gap-5">
                    <div className="text-center">
                      <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 16 }}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border border-sky-400/40 bg-sky-400/10 grid place-items-center"
                      >
                        <span className="font-display font-black text-4xl sm:text-5xl text-sky-300 text-glow-blue">
                          {myNum}
                        </span>
                      </motion.div>
                      <p className="mono text-[10px] uppercase tracking-widest text-sky-300 mt-2">{t.you}</p>
                    </div>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="font-display font-black text-2xl text-slate-500"
                    >
                      +
                    </motion.span>
                    <div className="text-center">
                      <motion.div
                        initial={{ scale: 0, rotate: 20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.25 }}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border border-amber-400/40 bg-amber-400/10 grid place-items-center"
                      >
                        <span className="font-display font-black text-4xl sm:text-5xl text-amber-300 text-glow-gold">
                          {aiNum}
                        </span>
                      </motion.div>
                      <p className="mono text-[10px] uppercase tracking-widest text-amber-300 mt-2">IA</p>
                    </div>
                  </div>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55 }}
                    className="font-display font-bold text-base sm:text-lg text-white"
                  >
                    {myNum} + {aiNum} = {sum} →{" "}
                    <span className={isEven ? "text-sky-300" : "text-amber-300"}>
                      {isEven ? t.pi_par : t.pi_impar}
                    </span>
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8, type: "spring", stiffness: 260, damping: 16 }}
                    className={`font-display font-black text-xl sm:text-2xl text-center ${youWin ? "text-sky-300" : "text-amber-300"}`}
                  >
                    {youWin ? t.st_win : t.pi_ai_took}
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <p data-testid="parimpar-status" className={`mono text-[11px] uppercase tracking-[0.15em] ${status.cls}`}>
              {status.text}
            </p>
            <button
              onClick={reset}
              data-testid="parimpar-reset-btn"
              className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-400/40 hover:rotate-[-120deg] transition-all duration-300"
              aria-label="Reiniciar partida"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          className="animate-float-slow absolute -top-6 -right-4 sm:-right-8 rounded-2xl border border-amber-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "4deg", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-amber-300">{t.in_dispute}</p>
          <p className="font-display font-bold text-white text-sm">{t.credits10}</p>
        </div>
        <div
          className="animate-float-slow absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl border border-sky-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "-5deg", animationDelay: "1.2s", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-sky-300">{t.pi_round}</p>
          <p className="font-display font-bold text-white text-sm">{t.pi_flash}</p>
        </div>
      </motion.div>
    </div>
  );
}
